<template>
  <main class="p-passwordchangesendmail">
    <section class="p-passwordchangesendmail__wrap">
      <h2 class="c-regist__title">{{ t('PasswordChangeSendMailPage.title') }}</h2>
      <RegisterFlow :steps="4" :nowStep="1" />
      <ul class="p-passwordchangesendmail__notes">
        <li class="p-passwordchangesendmail__note">
          {{ t('PasswordChangeSendMailPage.notes.note1') }}
        </li>
        <li class="p-passwordchangesendmail__note">
          {{ t(`PasswordChangeSendMailPage.notes.${isKokoBuild ? 'note2_koko' : 'note2'}`) }}
        </li>
        <li class="p-passwordchangesendmail__note">
          {{ t('PasswordChangeSendMailPage.notes.note3') }}
        </li>
      </ul>
      <dl class="p-passwordchangesendmail__lists">
        <div class="p-passwordchangesendmail__list">
          <dt class="p-passwordchangesendmail__list-title">
            <span class="-text">{{ t('PasswordChangeSendMailPage.userId') }}</span>
            <span class="-required">{{ t('PasswordChangeSendMailPage.must') }}</span>
          </dt>
          <dd>
            <input-text
              ref="inFieldUserId"
              v-model:input-value="passwordChangeStore.userId"
              class="p-passwordchangesendmail__list-data-item"
              :placeholder="t('PasswordChangeSendMailPage.userId')"
              :rules="userIdRules"
              name="userId"
            />
            <div v-if="maintenanceTitle && maintenanceMessage" class="p-inputerror__error">
              <div class="title">{{ maintenanceTitle }}</div>
              <div v-html="maintenanceMessage" class="message"></div>
            </div>
          </dd>
        </div>
      </dl>
      <div class="p-passwordchangesendmail__link">
        <button
          @click="onClickSendMail"
          :disabled="!isEnableButton"
          :class="{ isDisabled: !isEnableButton }"
          class="c-button"
        >
          <span class="c-button__text">{{
            t('PasswordChangeSendMailPage.passwordResetEmailButton')
          }}</span>
        </button>
      </div>
    </section>
  </main>
</template>

<script lang="ts">
import { defineComponent, reactive, computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import StoreUtil from '@/stores/StoreUtil'
import InputText from '@/components/common/field/InputText.vue'
import RegisterFlow from '@/components/registerTeam/RegisterFlow.vue'
import useMaintenanceStatus from '@/Hooks/useMaintenanceStatus'

export default defineComponent({
  name: 'PasswordChangeSendMailPage',
  setup() {
    const router = useRouter()
    const { t } = useI18n()
    const state = reactive({
      isSubmitting: false,
    })
    const inFieldUserId = ref<InstanceType<typeof InputText>>()
    const passwordChangeStore = StoreUtil.useStore('PasswordChangeStore')
    const messageDialogStore = StoreUtil.useStore('MessageDialogStore')
    const { fetchMaintenanceStatus, title, message } = useMaintenanceStatus()
    fetchMaintenanceStatus()

    const isEnableButton = computed(() => {
      return passwordChangeStore.userId && !state.isSubmitting && inFieldUserId.value?.meta.valid
    })
    const userIdRules = computed(() => {
      const rules = ['required']
      rules.push(passwordChangeStore.userId.indexOf('@') === -1 ? 'userIdFormat' : 'emailFormat')
      return rules.join('|')
    })

    /**
     * 教育向け（KOKO）ビルドかどうか
     */
    const isKokoBuild = computed(() => {
      return process.env.VUE_APP_BUILD_TARGET === 'koko'
    })

    return {
      state,
      isEnableButton,
      t,
      passwordChangeStore,
      userIdRules,
      inFieldUserId,
      isKokoBuild,
      async onClickSendMail() {
        if (state.isSubmitting) return
        state.isSubmitting = true
        const submitResult = await passwordChangeStore.getTokenAndSendResetPasswordMail()
        if (submitResult.isSuccess) {
          router.replace({ name: 'PasswordChangeSendMailCompletelyPage' })
          return
        }
        let dialogOptions = {}
        switch (submitResult.res.response.data.error_code) {
          case '401_0001': {
            dialogOptions = {
              title: t('PasswordChangeSendMailPage.dialog.authenticationError.title'),
              message: t(
                `PasswordChangeSendMailPage.dialog.authenticationError.${
                  isKokoBuild.value ? 'message_koko' : 'message'
                }`,
              ),
              status: 'error',
            }
            break
          }
          case '500_0018': {
            dialogOptions = {
              title: t('PasswordChangeSendMailPage.dialog.failedToSendMail.title'),
              message: t(
                `PasswordChangeSendMailPage.dialog.failedToSendMail.${
                  isKokoBuild.value ? 'message_koko' : 'message'
                }`,
              ),
              status: 'error',
            }
            break
          }
          case '500_0026': {
            dialogOptions = {
              title: t('PasswordChangeSendMailPage.dialog.failedToSendLine.title'),
              message: t('PasswordChangeSendMailPage.dialog.failedToSendLine.message'),
              status: 'error',
            }
            break
          }
          case 'NETWORK_ERROR': {
            dialogOptions = {
              title: t('dialog.netWorkError.title'),
              message: t('dialog.netWorkError.message'),
              status: 'error',
            }
            break
          }
          default: {
            dialogOptions = {
              title: t('dialog.unknownError.title'),
              message: t('dialog.unknownError.message', [
                submitResult.res.response.data.error_code,
              ]),
              status: 'error',
            }
            break
          }
        }
        await messageDialogStore.openWithSettings(dialogOptions)
        state.isSubmitting = false
      },
      maintenanceTitle: title,
      maintenanceMessage: message,
    }
  },
  components: {
    InputText,
    RegisterFlow,
  },
})
</script>

<style scoped lang="scss">
.isDisabled {
  opacity: 0.3;
  pointer-events: none;
}
</style>
